import React from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@asad/crane-ui'

/** Pole 6-cyfrowego kodu MFA — prezentacja z `app/prototypes`
 *  (`src/components/auth/MfaCodeInput.tsx`): pojedyncze komórki na prymitywie
 *  `Input` z `@asad/crane-ui`, autoFocus po zamontowaniu i po wyczyszczeniu
 *  (błędna próba), obsługa Backspace, strzałek i wklejenia całego kodu. */

type MfaCodeInputProps = {
  value: string
  onChange: (value: string) => void
  length?: number
}

export const MfaCodeInput = ({ value, onChange, length = 6 }: MfaCodeInputProps) => {
  const { t } = useTranslation()
  const refs = React.useRef<(HTMLInputElement | null)[]>([])
  const empty = value.length === 0

  React.useEffect(() => {
    if (empty) refs.current[0]?.focus()
  }, [empty])

  const setChar = (index: number, char: string) => {
    const digits = char.replace(/\D/g, '')
    if (!digits) return
    const next = (value.slice(0, index) + digits + value.slice(index + digits.length)).slice(0, length)
    onChange(next)
    refs.current[Math.min(next.length, length - 1)]?.focus()
  }

  const onKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (value[index]) {
        onChange(value.slice(0, index) + value.slice(index + 1))
      } else if (index > 0) {
        onChange(value.slice(0, index - 1) + value.slice(index))
        refs.current[index - 1]?.focus()
      }
    }
    if (event.key === 'ArrowLeft' && index > 0) refs.current[index - 1]?.focus()
    if (event.key === 'ArrowRight' && index < length - 1) refs.current[index + 1]?.focus()
  }

  const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const digits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (digits) {
      onChange(digits)
      refs.current[Math.min(digits.length, length - 1)]?.focus()
    }
  }

  return (
    <div className='flex justify-center gap-1 md:gap-2' role='group' aria-label={t('mfaVerify.codeInputLabel')}>
      {Array.from({ length }, (_, index) => (
        <Input
          key={index}
          ref={(el) => {
            refs.current[index] = el
          }}
          className='h-12 min-w-0 max-w-12 flex-1 px-0 md:w-10 md:max-w-none md:flex-none text-center text-lg font-semibold tabular-nums'
          type='text'
          inputMode='numeric'
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={value[index] ?? ''}
          onChange={(event) => setChar(index, event.target.value)}
          onKeyDown={(event) => onKeyDown(index, event)}
          onPaste={onPaste}
          aria-label={t('mfaVerify.codeDigit', { index: index + 1 })}
        />
      ))}
    </div>
  )
}
